import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EventPublication } from 'src/app/Models/Classes/Publications/Event/event-publication';
import { NormalPublication } from 'src/app/Models/Classes/Publications/Normal/normal-publication';
import { PublicationClass } from 'src/app/Models/Classes/Publications/publication-class';
import { StoryPublication } from 'src/app/Models/Classes/Publications/Story/story-publication';
import { UrlPublication } from 'src/app/Models/Classes/Publications/Url/url-publication';
import { ReactionContainerClass } from 'src/app/Models/Classes/Reactions/ReactionContainer/reaction-container-class';
import { ReactionClass } from 'src/app/Models/Classes/Reactions/SingleReaction/reaction-class';
import { SellerProfile } from 'src/app/Models/Classes/User/Store/seller-profile';
import { RequestResponse } from '../../Models/Classes/request-response';
import { RequestService } from '../request/request.service';
import { newnormal } from 'src/app/Models/Classes/Publications/New-Normal/newnormal';
import { LkfilmPublication } from 'src/app/Models/Classes/Publications/Lkfilm/lkfilm-publication';
import { Eventos } from 'src/app/Models/Classes/Publications/Eventos/eventos';
import { Land } from 'src/app/Models/Classes/Publications/Land/land';
import { Epoch } from 'src/app/Models/Classes/Publications/Epoch/epoch';
import { Earth } from 'src/app/Models/Classes/Publications/Earth/earth';
import { Capacitor } from '@capacitor/core';
import { LkmusicPublication } from 'src/app/Models/Classes/Publications/lk-music/lkmusic-publication';
import { Feex } from 'src/app/Models/Classes/Publications/Feex/feex';
import { SocialInteractionNotificationService } from '../notifications/social-interaction-notification.service';

interface StoryAuthorIdentity {
	userIds: string[];
	profileIds: string[];
	usernames: string[];
	photos: string[];
}

const STORY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const STORY_FUTURE_SKEW_MS = 5 * 60 * 1000;

@Injectable({
	providedIn: 'root'
})
export class PublicationService {

	public UserProfileList = new Array<SellerProfile>();
	public GlobalPublicationsList = new Array<PublicationClass>();
	public ReactionList = new Array<ReactionClass>();

	public MyStoriesList = new Array<PublicationClass>();
	public readonly storiesChanged$ = new Subject<Array<PublicationClass>>();
	private storiesLoadPromise?: Promise<Array<PublicationClass>>;
	private storiesLoaded = false;
	private storiesReloadRequested = false;
	private authorStoriesLoadPromises = new Map<string, Promise<Array<PublicationClass>>>();
	private profileIdLoadPromises = new Map<string, Promise<string | null>>();
	private readonly storySeenStoragePrefix = 'laniakea.story-seen.';
	private readonly storySeenCache = new Map<string, Set<string>>();
	private readonly storySeenRequests = new Set<string>();
	public MyFeedList = new Array<PublicationClass>();

	public MylkfilmList = new Array<PublicationClass>();
	public MylandList = new Array<PublicationClass>();
	public MyepochList = new Array<PublicationClass>();
	public MyeventosList = new Array<PublicationClass>();
	public MyearthList = new Array<PublicationClass>();

	public MyPublicationsList = new Array<PublicationClass>();
	public TemporalPublicationList = new Array<PublicationClass>();
	public StatusPublicationList = new Array<PublicationClass>();
	public COLECCIONCOMPRASLIST = new Array<PublicationClass>();
	public COLECCIONREGALOSLIST = new Array<PublicationClass>();
	constructor(
		private request: RequestService,
		private socialNotifications: SocialInteractionNotificationService,
	) { }

	public postPublication(publication: any): Promise<RequestResponse> {
		return new Promise((resolve, reject) => {
			this.request.createRequestPost('publications', publication).then((data) => {
				this.socialNotifications.notifyLocal(
					'Publicación creada',
					'Tu publicación ya está disponible en Laniakea.',
					{ event_type: 'publication_created' },
				);

				resolve(data);
			}).catch((error) => {
				//console.error(error);
				reject(error);
			});
		});
	}

	public getPublications(id_publication?: number): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			var params;
			id_publication ? params = { "id": id_publication } : params = null;

			this.request.createRequestGet('publications', null, params).then((data) => {

				if (data == "") {
					reject("Unvaliable");
				}
				else {
					resolve(JSON.parse(data));
				}

			}).catch((error) => {

				reject(error);
			})
		});
	}

	public getStories(id_publication?: number): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			// This is the shared, all-authors Stories endpoint. Keep the global
			// collection on this route; `get_stories_by_user` is intentionally
			// reserved for the profile-scoped fallback below. Passing a null profile
			// to that endpoint is not equivalent across API deployments and can
			// silently return only the authenticated user's Stories.
			this.request.createRequestGet('stories', null).then((data) => {
				try {
					const payload = this.parseStoryPayload(data);
					const activeStories = this.getActiveStories(this.extractStoryRecords(payload));
					resolve(activeStories as any);
				} catch (error) {
					reject(error);
				}
			}).catch(reject);
		});
	}

	/**
	 * Load the shared Stories collection once so profile surfaces can use the
	 * same records as the Stories bar without issuing one request per avatar.
	 */
	public loadStories(force = false): Promise<Array<PublicationClass>> {
		// MyStoriesList can contain a locally-created Story before the shared
		// request completes. Its length is therefore not proof that the global
		// collection has been loaded; using it here hides other authors' Stories.
		if (!force && this.storiesLoaded) {
			this.pruneInactiveStories();
			return Promise.resolve(this.MyStoriesList);
		}

		if (this.storiesLoadPromise) {
			if (force) {
				// A profile/search surface can become ready while the first request
				// is still pending. Queue one refresh instead of silently dropping it.
				this.storiesReloadRequested = true;
			}
			return this.storiesLoadPromise;
		}

		return this.startStoriesLoad();
	}

	private startStoriesLoad(): Promise<Array<PublicationClass>> {
		let storiesLoadPromise: Promise<Array<PublicationClass>>;
		storiesLoadPromise = this.getStories()
			.then((data: any) => {
				const records = this.getActiveStories(this.extractStoryRecords(data))
					.filter(record => record && typeof record === 'object');

				// A successful global response is the authoritative collection for the
				// current authenticated viewer. Rebuild it instead of merging into the
				// previous array: otherwise a forced refresh can retain Stories from a
				// previous account or stale author records that the API no longer returns.
				this.MyStoriesList = [];
				this.storiesChanged$.next(this.MyStoriesList);

				return Promise.all(records.map((record: any) =>
					this.updatePublicationList('MyStoriesList', record, true).catch(error => {
						// One malformed/legacy Story must not hide every other author's
						// Story from the avatars.
						console.warn('Skipping an invalid Story record:', error);
						return null;
					})
				)).then(() => {
					this.pruneInactiveStories();
					this.storiesLoaded = true;
					return this.MyStoriesList;
				});
			})
			.finally(() => {
				if (this.storiesLoadPromise !== storiesLoadPromise) {
					return;
				}

				this.storiesLoadPromise = undefined;
				if (this.storiesReloadRequested) {
					this.storiesReloadRequested = false;
					this.startStoriesLoad().catch(error => {
						console.warn('Queued Stories refresh failed:', error);
					});
				}
			});

		this.storiesLoadPromise = storiesLoadPromise;
		return storiesLoadPromise;
	}

	/**
	 * Resolve one author's Stories for surfaces that only know that profile.
	 * The global status feed is preferred; the existing profile Stories
	 * endpoint is used as a targeted fallback when the global payload cannot
	 * be matched by ID, username, or photo. Requests are cached per profile so
	 * a search page with many avatars does not issue duplicate calls.
	 */
	public loadStoriesForAuthor(
		profileId?: any,
		authorId?: any,
		username?: any,
		profilePhoto?: any,
		sharedOnly = false,
	): Promise<Array<PublicationClass>> {
		const findStories = () => this.getStoriesForAuthor(authorId, username, profilePhoto, profileId);
		const alreadyLoaded = findStories();
		if (alreadyLoaded.length) {
			return Promise.resolve(alreadyLoaded);
		}

		return this.loadStories()
			.catch(error => {
				// The global collection is an optimization. A failed global request
				// must not prevent the profile-scoped fallback below.
				console.warn('Global Stories lookup unavailable; using author lookup:', error);
				return this.MyStoriesList;
			})
			.then(() => {
			const storiesAfterGlobalLoad = findStories();
			if (storiesAfterGlobalLoad.length) {
				return storiesAfterGlobalLoad;
			}

			// Mycelium renders many avatars at once. Its shared Stories collection is
			// the source used by the Stories bar, so avoid a profile lookup for every
			// avatar when the author has no Story in that collection.
			if (sharedOnly) {
				return storiesAfterGlobalLoad;
			}

			const explicitProfileId = this.normalizeStoryId(profileId);
			if (explicitProfileId) {
				return this.loadStoriesByProfile(
					explicitProfileId,
					authorId,
					username,
					profilePhoto,
				);
			}

			// Publication headers usually have a user_id but not a profile_id.
			// Resolve that mapping only when the global collection did not match.
			const requestedAuthorId = this.firstStoryId(authorId);
			if (!requestedAuthorId) {
				return storiesAfterGlobalLoad;
			}

			return this.resolveProfileIdForUser(requestedAuthorId).then(resolvedProfileId =>
				resolvedProfileId
					? this.loadStoriesByProfile(resolvedProfileId, authorId, username, profilePhoto)
					: findStories()
			);
		});
	}

	private loadStoriesByProfile(
		profileId: string,
		authorId?: any,
		username?: any,
		profilePhoto?: any,
	): Promise<Array<PublicationClass>> {
		const existingRequest = this.authorStoriesLoadPromises.get(profileId);
		if (existingRequest) {
			return existingRequest.then(() => this.getStoriesForAuthor(authorId, username, profilePhoto, profileId));
		}

		const requestPromise = this.request.createRequestPost('get_stories_by_user', { profile_id: profileId })
			.then((data: any) => {
				const records = this.getActiveStories(this.extractStoryRecords(this.parseStoryPayload(data)))
					.filter(record => record && typeof record === 'object');

				return Promise.all(records.map((record: any) =>
					this.updatePublicationList('MyStoriesList', record, true).catch(error => {
						console.warn('Skipping an invalid author Story record:', error);
						return null;
					})
				)).then(loadedStories => {
					// The endpoint is already scoped to this profile. Return its records
					// even when an old payload omits the author's identity fields.
					const directStories = this.getActiveStories(loadedStories.filter(Boolean));
					return directStories.length
						? directStories
						: this.getStoriesForAuthor(authorId, username, profilePhoto, profileId);
				});
			})
			.catch(error => {
				console.warn('Unable to load Stories for profile:', profileId, error);
				return this.getStoriesForAuthor(authorId, username, profilePhoto, profileId);
			})
			.finally(() => {
				this.authorStoriesLoadPromises.delete(profileId);
			});

		this.authorStoriesLoadPromises.set(profileId, requestPromise);
		return requestPromise;
	}

	private resolveProfileIdForUser(userId: string): Promise<string | null> {
		const existingRequest = this.profileIdLoadPromises.get(userId);
		if (existingRequest) {
			return existingRequest;
		}

		const requestPromise = this.request.createRequestPost('getProfileByUser_Id', { user_id: userId })
			.then((data: any) => {
				const profileData = this.parseStoryPayload(data);
				return this.normalizeStoryId(
					profileData?.profileData?.id
					?? profileData?.profile?.id
					?? profileData?.id
				);
			})
			.catch(error => {
				console.warn('Unable to resolve profile ID for Story author:', userId, error);
				return null;
			})
			.finally(() => {
				this.profileIdLoadPromises.delete(userId);
			});

		this.profileIdLoadPromises.set(userId, requestPromise);
		return requestPromise;
	}

	/** Return Stories for one author without mixing user and profile ID namespaces. */
	public getStoriesForAuthor(
		authorId?: any,
		username?: any,
		profilePhoto?: any,
		profileId?: any,
		stories: Array<any> = this.MyStoriesList,
	): Array<PublicationClass> {
		const requested: StoryAuthorIdentity = {
			userIds: this.normalizeStoryIdList(authorId),
			profileIds: this.normalizeStoryIdList(profileId),
			usernames: this.normalizeStoryUsernameList(username),
			photos: this.normalizeStoryPhotoList(profilePhoto),
		};

		const availableStories = Array.isArray(stories) ? stories : this.MyStoriesList;
		return this.getActiveStories(availableStories)
			.filter(story => this.storyMatchesIdentity(story, requested));
	}

	private storyMatchesIdentity(story: any, requested: StoryAuthorIdentity): boolean {
		if (!story) {
			return false;
		}

		const actual = this.extractStoryAuthorIdentity(story);
		const requestedHasStrongId = requested.userIds.length > 0 || requested.profileIds.length > 0;
		const actualHasStrongId = actual.userIds.length > 0 || actual.profileIds.length > 0;
		const userMatches = requested.userIds.some(id => actual.userIds.includes(id));
		const profileMatches = requested.profileIds.some(id => actual.profileIds.includes(id));
		const usernameMatches = requested.usernames.some(name => actual.usernames.includes(name));

		// Usernames are unique canonical author handles. They are the safest
		// recovery path when a legacy feed row contains a stale numeric ID, for
		// example when a comment's nested user was accidentally used as the post
		// author's ID. Do this before rejecting an ID conflict, but only after the
		// Story extractor has restricted actual.usernames to authoritative fields.
		if (usernameMatches) {
			return true;
		}

		// Never compare a profile ID with a user ID. A known conflicting ID in
		// the same namespace is authoritative and prevents cross-author matches.
		if (requested.userIds.length && actual.userIds.length && !userMatches) {
			return false;
		}
		if (userMatches) {
			return true;
		}
		if (requested.profileIds.length && actual.profileIds.length && !profileMatches) {
			return false;
		}
		if (profileMatches) {
			return true;
		}

		// A photo is only a last-resort match when both records lack IDs.
		return !requestedHasStrongId
			&& !actualHasStrongId
			&& requested.photos.some(photo => actual.photos.includes(photo));
	}

	private extractStoryAuthorIdentity(story: any): StoryAuthorIdentity {
		// A Story payload can contain nested users from an embedded publication,
		// comment, or integration. The publication-level author fields are the
		// authoritative owner; do not union unrelated nested IDs with them.
		const explicitUserIds = this.normalizeStoryIdList([
			story?.user_id,
			story?.userId,
			story?.author_id,
			story?.authorId,
			story?.userProfile?.user_id,
			story?.userProfile?.userId,
			story?.user_profile?.user_id,
			story?.user_profile?.userId,
			story?.__storyAuthor?.user_id,
			story?.__storyAuthor?.userId,
		]);
		const userIds = explicitUserIds.length ? explicitUserIds : this.normalizeStoryIdList([
			story?.user?.user_id,
			story?.user?.userId,
			story?.user?.id,
			story?.author?.user_id,
			story?.author?.userId,
			story?.author?.id,
			story?.user_info?.user_id,
			story?.user_info?.userId,
			story?.user_info?.id,
			story?.userInfo?.user_id,
			story?.userInfo?.userId,
			story?.userInfo?.id,
			story?.__storyAuthor?.user?.id,
			story?.__storyAuthor?.author?.id,
		]);
		const explicitProfileIds = this.normalizeStoryIdList([
			story?.profile_id,
			story?.profileId,
			story?.userProfile?.profile_id,
			story?.userProfile?.profileId,
			story?.user_profile?.profile_id,
			story?.user_profile?.profileId,
			story?.user?.profile_id,
			story?.user?.profileId,
			story?.author?.profile_id,
			story?.author?.profileId,
			story?.user_info?.profile_id,
			story?.user_info?.profileId,
			story?.userInfo?.profile_id,
			story?.userInfo?.profileId,
			story?.__storyAuthor?.profile_id,
			story?.__storyAuthor?.profileId,
		]);
		const profileIds = explicitProfileIds.length ? explicitProfileIds : this.normalizeStoryIdList([
			story?.profile?.id,
			story?.profile?.profile_id,
			story?.profile?.profileId,
			story?.user?.profile?.id,
			story?.user?.profile?.profile_id,
			story?.user?.profile?.profileId,
			story?.author?.profile?.id,
			story?.author?.profile?.profile_id,
			story?.author?.profile?.profileId,
			story?.user_info?.profile?.id,
			story?.user_info?.profile?.profile_id,
			story?.user_info?.profile?.profileId,
			story?.userInfo?.profile?.id,
			story?.userInfo?.profile?.profile_id,
			story?.userInfo?.profile?.profileId,
			story?.__storyAuthor?.profile?.id,
		]);
		const authorSources = [
			story?.userProfile,
			story?.user_profile,
			story?.user,
			story?.author,
			story?.profile,
			story?.user_info,
			story?.userInfo,
			story?.__storyAuthor,
		].filter(source => source && typeof source === 'object');
		const explicitUsernames = this.normalizeStoryUsernameList([
			story?.username,
			story?.userName,
			story?.__storyAuthor?.username,
			story?.__storyAuthor?.userName,
		]);
		const usernames = explicitUsernames.length ? explicitUsernames : this.normalizeStoryUsernameList([
			...authorSources.reduce((values: any[], source: any) => values.concat([
				source?.username,
				source?.userName,
				source?.user?.username,
				source?.user?.userName,
				source?.author?.username,
				source?.author?.userName,
			]), []),
		]);
		const photos = this.normalizeStoryPhotoList([
			story?.profile_photo,
			story?.profilePhoto,
			story?.user_photo,
			story?.userPhoto,
			...authorSources.reduce((values: any[], source: any) => values.concat([
				source?.profile_photo,
				source?.profilePhoto,
				source?.user_photo,
				source?.userPhoto,
				source?.photo,
				source?.avatar,
				source?.image,
			]), []),
		]);

		return { userIds, profileIds, usernames, photos };
	}

	private normalizeStoryIdList(value: any): string[] {
		const values = Array.isArray(value) ? value : [value];
		return Array.from(new Set(values.map(candidate => this.normalizeStoryId(candidate)).filter(Boolean)));
	}

	private normalizeStoryUsernameList(value: any): string[] {
		const values = Array.isArray(value) ? value : [value];
		return Array.from(new Set(values.map(candidate => this.normalizeStoryUsername(candidate)).filter(Boolean)));
	}

	private normalizeStoryPhotoList(value: any): string[] {
		const values = Array.isArray(value) ? value : [value];
		return Array.from(new Set(values.map(candidate => this.normalizeStoryPhoto(candidate)).filter(Boolean)));
	}

	public getActiveStories(stories: Array<any> | null | undefined): Array<any> {
		return (Array.isArray(stories) ? stories : []).filter(story => this.isActiveStory(story));
	}

	private pruneInactiveStories(): void {
		const activeStories = this.getActiveStories(this.MyStoriesList) as Array<PublicationClass>;
		if (activeStories.length === this.MyStoriesList.length) {
			return;
		}

		this.MyStoriesList = activeStories;
		this.storiesChanged$.next(this.MyStoriesList);
	}

	private isActiveStory(story: any): boolean {
		const now = Date.now();
		const stateWindows = [
			...(Array.isArray(story?.container?.states) ? story.container.states : []),
			...(Array.isArray(story?.__storyAuthor?.container?.states) ? story.__storyAuthor.container.states : []),
		];
		if (stateWindows.some(state => this.isActiveStoryWindow(
			state?.timestamp_start ?? state?.timestampStart,
			state?.timestamp_finish ?? state?.timestampFinish,
			now,
		))) {
			return true;
		}

		const storyStart = this.firstStoryTimestamp([
			story?.timestamp_start,
			story?.timestampStart,
			story?.container?.timestamp_start,
			story?.container?.timestampStart,
			story?.__storyAuthor?.timestamp_start,
			story?.__storyAuthor?.timestampStart,
			story?.__storyAuthor?.container?.timestamp_start,
			story?.__storyAuthor?.container?.timestampStart,
		]);
		const storyFinish = this.firstStoryTimestamp([
			story?.timestamp_finish,
			story?.timestampFinish,
			story?.container?.timestamp_finish,
			story?.container?.timestampFinish,
			story?.__storyAuthor?.timestamp_finish,
			story?.__storyAuthor?.timestampFinish,
			story?.__storyAuthor?.container?.timestamp_finish,
			story?.__storyAuthor?.container?.timestampFinish,
		]);
		if (storyStart || storyFinish) {
			return this.isActiveStoryWindow(storyStart, storyFinish, now);
		}

		// Legacy payloads without a state lifetime are only safe when their
		// publication creation time also falls inside the same 24-hour window.
		// Never trust an endpoint response alone to revive an old Story.
		const timestamp = [
			story?.createdAt,
			story?.created_at,
			story?.createdAtTimestamp,
			story?.__storyAuthor?.created_at,
			story?.__storyAuthor?.createdAt,
		]
			.map(value => Number(this.storyTimestamp(value)))
			.find(value => Number.isFinite(value) && value > 0);
		if (!Number.isFinite(timestamp) || timestamp <= 0) {
			return false;
		}

		const age = now - timestamp;
		return age >= -STORY_FUTURE_SKEW_MS && age <= STORY_MAX_AGE_MS;
	}

	private firstStoryTimestamp(values: any[]): number {
		return values
			.map(value => Number(this.storyTimestamp(value)))
			.find(value => Number.isFinite(value) && value > 0) || 0;
	}

	private isActiveStoryWindow(startValue: any, finishValue: any, now: number): boolean {
		const start = this.firstStoryTimestamp([startValue]);
		const finish = this.firstStoryTimestamp([finishValue]);
		if (!start && !finish) {
			return false;
		}

		if (start && start - now > STORY_FUTURE_SKEW_MS) {
			return false;
		}

		if (finish) {
			return now <= finish;
		}

		return now - start <= STORY_MAX_AGE_MS;
	}

	/**
	 * Stories use the same publication view record as the regular feed. Keep a
	 * small browser/native fallback as well so the ring updates immediately and
	 * remains useful during a rolling API deployment or a temporary network
	 * failure.
	 */
	public isStorySeen(story: any, viewerProfile?: any): boolean {
		if (this.isStorySeenFromServer(story)) {
			return true;
		}

		const storyKey = this.storySeenIdentity(story);
		return !!storyKey && this.getStorySeenSet(this.storyViewerKey(viewerProfile)).has(storyKey);
	}

	public hasUnseenStories(stories: Array<any>, viewerProfile?: any): boolean {
		const availableStories = Array.isArray(stories) ? stories.filter(Boolean) : [];
		return availableStories.some(story => !this.isStorySeen(story, viewerProfile));
	}

	public areAllStoriesSeen(stories: Array<any>, viewerProfile?: any): boolean {
		const availableStories = Array.isArray(stories) ? stories.filter(Boolean) : [];
		return availableStories.length > 0 && !this.hasUnseenStories(availableStories, viewerProfile);
	}

	/** Mark one Story publication as seen without removing it from any viewer. */
	public markStoryAsSeen(story: any, viewerProfile?: any): void {
		const storyKey = this.storySeenIdentity(story);
		if (!storyKey) {
			return;
		}

		const viewerKey = this.storyViewerKey(viewerProfile);
		const seenStories = this.getStorySeenSet(viewerKey);
		const wasSeen = seenStories.has(storyKey) || this.isStorySeenFromServer(story);
		seenStories.add(storyKey);
		this.persistStorySeenSet(viewerKey, seenStories);

		const seenAt = story?.seen_at ?? story?.seenAt ?? Math.floor(Date.now() / 1000);
		if (story && typeof story === 'object') {
			story.seen = true;
			story.is_seen = true;
			story.seen_at = seenAt;
		}

		const storyInList = this.MyStoriesList.find(candidate =>
			candidate === story || this.storySeenIdentity(candidate) === storyKey
		) as any;
		if (storyInList && storyInList !== story) {
			storyInList.seen = true;
			storyInList.is_seen = true;
			storyInList.seen_at = storyInList.seen_at ?? seenAt;
		}

		// Notify all avatar surfaces only when the visible state actually changed.
		if (!wasSeen || story?.seen !== true) {
			this.storiesChanged$.next(this.MyStoriesList);
		}

		const publicationId = this.storyPublicationId(story);
		if (!publicationId || viewerKey === 'viewer:anonymous') {
			return;
		}

		const requestKey = `${viewerKey}:${storyKey}`;
		if (this.storySeenRequests.has(requestKey)) {
			return;
		}

		this.storySeenRequests.add(requestKey);
		this.request.createRequestPost('publications/seen', { publication_id: publicationId })
			.catch(error => {
				// The optimistic local record remains authoritative for this session;
				// a later Stories response can still reconcile with the server.
				console.warn('Unable to persist Story view:', error);
			})
			.finally(() => this.storySeenRequests.delete(requestKey));
	}

	private isStorySeenFromServer(story: any): boolean {
		return [
			story?.seen,
			story?.is_seen,
			story?.isSeen,
			story?.viewed,
			story?.is_viewed,
			story?.isViewed,
			story?.container?.seen,
			story?.container?.is_seen,
			story?.container?.isSeen,
			story?.container?.viewed,
			story?.container?.is_viewed,
			story?.container?.isViewed,
		].some(value => this.isTruthyStoryFlag(value)) || [
			story?.seen_at,
			story?.seenAt,
			story?.viewed_at,
			story?.viewedAt,
			story?.container?.seen_at,
			story?.container?.seenAt,
		].some(value => value !== undefined && value !== null && value !== '');
	}

	private isTruthyStoryFlag(value: any): boolean {
		return value === true || value === 1 || value === '1' || value === 'true';
	}

	private storyPublicationId(story: any): number | null {
		const rawId = [
			story?.id,
			story?.publication_id,
			story?.publicationId,
			story?.story_id,
			story?.storyId,
		].find(value =>
			(typeof value === 'number' && Number.isFinite(value)) ||
			(typeof value === 'string' && value.trim() !== '')
		);
		const publicationId = Number(rawId);
		return Number.isFinite(publicationId) && publicationId > 0 ? Math.trunc(publicationId) : null;
	}

	private storySeenIdentity(story: any): string {
		const rawId = [
			story?.id,
			story?.publication_id,
			story?.publicationId,
			story?.story_id,
			story?.storyId,
		].find(value =>
			(typeof value === 'number' && Number.isFinite(value)) ||
			(typeof value === 'string' && value.trim() !== '')
		);
		if (rawId !== undefined && rawId !== null) {
			return `publication:${String(rawId).trim()}`;
		}

		const createdAt = story?.createdAt
			?? story?.created_at
			?? story?.__storyAuthor?.created_at
			?? story?.__storyAuthor?.createdAt;
		const storyTimestamp = this.storyTimestamp(createdAt);
		const username = this.normalizeStoryUsername(
			story?.username
				?? story?.userName
				?? story?.author?.username
				?? story?.user?.username
				?? story?.userProfile?.username
		);
		return username && storyTimestamp ? `fallback:${username}:${storyTimestamp}` : '';
	}

	private storyTimestamp(value: any): string {
		if (value instanceof Date) {
			return String(value.getTime());
		}

		if (typeof value === 'number' && Number.isFinite(value)) {
			return String(Math.round(value < 100000000000 ? value * 1000 : value));
		}

		if (typeof value !== 'string' || !value.trim()) {
			return '';
		}

		const numericValue = Number(value);
		if (Number.isFinite(numericValue)) {
			return String(Math.round(numericValue < 100000000000 ? numericValue * 1000 : numericValue));
		}

		const parsedValue = Date.parse(value);
		return Number.isFinite(parsedValue) ? String(parsedValue) : '';
	}

	private storyViewerKey(viewerProfile: any): string {
		const profile = viewerProfile || {};
		const nestedProfile = profile.user || profile.profile || {};
		const rawId = [
			profile.user_id,
			profile.userId,
			profile.user?.user_id,
			profile.user?.userId,
			profile.user?.id,
			profile.id,
			profile.profile_id,
			profile.profileId,
			nestedProfile.user_id,
			nestedProfile.userId,
			nestedProfile.id,
		].find(value =>
			(typeof value === 'number' && Number.isFinite(value)) ||
			(typeof value === 'string' && value.trim() !== '')
		);
		const normalizedId = rawId === undefined || rawId === null ? '' : String(rawId).trim();
		return `viewer:${normalizedId || 'anonymous'}`;
	}

	private getStorySeenSet(viewerKey: string): Set<string> {
		const cached = this.storySeenCache.get(viewerKey);
		if (cached) {
			return cached;
		}

		const seenStories = new Set<string>();
		try {
			if (typeof localStorage !== 'undefined') {
				const rawValue = localStorage.getItem(this.storySeenStorageKey(viewerKey));
				if (rawValue) {
					const parsedValue = JSON.parse(rawValue);
					const storedIds = Array.isArray(parsedValue) ? parsedValue : parsedValue?.ids;
					if (Array.isArray(storedIds)) {
						storedIds.filter(id => typeof id === 'string').forEach(id => seenStories.add(id));
					}
				}
			}
		} catch {
			// Storage can be disabled or unavailable in a private/native webview.
		}

		this.storySeenCache.set(viewerKey, seenStories);
		return seenStories;
	}

	private persistStorySeenSet(viewerKey: string, seenStories: Set<string>): void {
		try {
			if (typeof localStorage !== 'undefined') {
				const ids = Array.from(seenStories).slice(-2000);
				localStorage.setItem(this.storySeenStorageKey(viewerKey), JSON.stringify(ids));
			}
		} catch {
			// The in-memory cache still provides the immediate indicator.
		}
	}

	private storySeenStorageKey(viewerKey: string): string {
		return `${this.storySeenStoragePrefix}${encodeURIComponent(viewerKey)}`;
	}

	private extractStoryRecords(data: any): Array<any> {
		if (Array.isArray(data)) {
			return data;
		}

		const collection = data?.data ?? data?.stories ?? data?.results ?? data?.success;
		if (Array.isArray(collection)) {
			return collection;
		}

		if (collection && typeof collection === 'object') {
			return Object.values(collection).filter(record => record && typeof record === 'object');
		}

		if (data && typeof data === 'object' && (data.publication_type || data.container || data.id)) {
			return [data];
		}

		return data && typeof data === 'object'
			? Object.values(data).filter(record => record && typeof record === 'object')
			: [];
	}

	private parseStoryPayload(data: any): any {
		let payload = data;
		// Browser and native transports can produce one or two JSON strings.
		for (let attempt = 0; attempt < 3 && typeof payload === 'string'; attempt++) {
			const value = payload.trim();
			if (!value) {
				return [];
			}
			try {
				payload = JSON.parse(value);
			} catch {
				return data;
			}
		}
		return payload;
	}

	private firstStoryId(value: any): string {
		const values = Array.isArray(value) ? value : [value];
		return values
			.map(candidate => this.normalizeStoryId(candidate))
			.find(Boolean) || '';
	}

	private normalizeStoryId(value: any): string {
		if (value === null || value === undefined) {
			return '';
		}

		const normalized = String(value).trim();
		return normalized && normalized !== 'null' && normalized !== 'undefined' ? normalized : '';
	}

	private normalizeStoryUsername(value: any): string {
		if (typeof value !== 'string') {
			return '';
		}

		const normalized = value.trim().replace(/^@+/, '').toLocaleLowerCase();
		return normalized && normalized !== 'null' && normalized !== 'undefined' ? normalized : '';
	}

	private normalizeStoryPhoto(value: any): string {
		let source = value;
		for (let depth = 0; depth < 3 && source && typeof source === 'object'; depth++) {
			source = source.url
				|| source.route
				|| source.path
				|| source.src
				|| source.photo_url
				|| source.photoUrl
				|| source.profile_photo
				|| source.profilePhoto;
		}

		if (typeof source !== 'string') {
			return '';
		}

		const photo = source.trim().toLocaleLowerCase();
		if (!photo || photo === 'null' || photo === 'undefined' || /(^|\/)profile_default\.png(?:[?#].*)?$/i.test(photo)) {
			return '';
		}

		try {
			const parsed = new URL(photo, 'https://laniakea.invalid');
			return parsed.pathname.replace(/^\/+/, '');
		} catch {
			return photo.replace(/^\/+/, '').split(/[?#]/)[0];
		}
	}

	public getAllPublications(page?: number, user_id?: number, publication_type?: string): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let user_id_str = (user_id) ? user_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let publication_type_str = (publication_type) ? publication_type : "";
			var params = {
					"page": page_str,
					"user_id": user_id_str,
          "publication_type": publication_type_str
			};


			this.request.createRequestGet('publications', params).then((data) => {
				try {
					let json = JSON.parse(data);
					if(json){
        //    console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}).catch((error) => {
				reject(error);
			})
		});
	}

	public putPublication(publication: any, id_publication: number): Promise<RequestResponse> {

		return new Promise((resolve, reject) => {
			var params = { "id": id_publication };
			this.request.createRequestPut('publications', publication, params).then((data) => {
				resolve(data);
			}).catch((error) => {
				reject(error);
			});
		});
	}

	public deletePublications(id_publication: number): Promise<RequestResponse> {
		return new Promise((resolve, reject) => {
			var params = { "id": id_publication };
			this.request.createRequestPost('DeletePublication', params).then((client) => {
				this.DeleteLocalPublicationByID(id_publication);
				resolve(client);
			}).catch((error) => {
				reject(error);
			})
		});
	}

	public postSavePublication(id_publication: number) {
		return new Promise((resolve, reject) => {
			let params = { "id": id_publication };
			this.request.createRequestPost('publications', {}, params, 'save').then((data) => {
				resolve(data);
			}).catch((error) => {
				reject(error);
			});

		});
	}

	public deleteSavePublication(id_publication: number) {
		return new Promise((resolve, reject) => {
			var params = {
				"id": id_publication,
				"id2": 0
			}
			this.request.createRequestDelete('publications', null, params, 'save').then((client) => {
				resolve(client);
			}).catch((error) => {
				reject(error);
			});
		});
	}

	public getSavedPublications(page?: number) {
		return new Promise((resolve, reject) => {
			let page_str;
			page ? page_str = page.toString() : page_str = "1";
			let publication_id = 0;

			var params = {
				'id': publication_id.toString(),
				'queryParams': {
					"page": page_str
				}
			};

			this.request.createRequestGet('publications', params.queryParams, params, 'save').then((data) => {
				resolve(JSON.parse(data));
			}).catch((error) => {
				reject(error);
			});

		});
	}

	public postBuyPublication(id_publication: number) {
		return new Promise((resolve, reject) => {
			let params = { "id": id_publication };
			this.request.createRequestPost('publications', {}, params, 'payment').then((data) => {
				resolve(data);
			}).catch((error) => {
				reject(error);
			});

		});
	}

	public getUserProfile_Publications(objectPublication: any) {

		return new Promise<SellerProfile>((resolve, reject) => {

			if (objectPublication == null) {
				resolve(null); // Resolve with null instead of rejecting
			}

			const firstDefined = (...values: any[]) => values.find(value =>
				value !== null &&
				value !== undefined &&
				!(typeof value === 'string' && !value.trim())
			);
			const storyUserSources = [
				objectPublication.user,
				objectPublication.author,
				objectPublication.userProfile,
				objectPublication.user_profile,
				objectPublication.profile,
				objectPublication.user_info,
				objectPublication.userInfo,
			].filter(source => source && typeof source === 'object');
			const storyUsers = storyUserSources.reduce((sources: any[], source: any) => sources.concat([
				source,
				source.user,
				source.author,
			]), []);
			const userId = firstDefined(
				objectPublication.user_id,
				objectPublication.userId,
				objectPublication.author_id,
				objectPublication.authorId,
				...storyUsers.reduce((values: any[], source: any) => values.concat([
					source?.user_id,
					source?.userId,
					source?.user?.user_id,
					source?.user?.userId,
					source?.user?.id,
					source?.author?.user_id,
					source?.author?.userId,
					source?.author?.id,
				]), []),
			);
			const profileId = firstDefined(
				objectPublication.profile_id,
				objectPublication.profileId,
				...storyUsers.reduce((values: any[], source: any) => values.concat([
					source?.profile_id,
					source?.profileId,
					source?.profile?.id,
				]), []),
			);
			const username = firstDefined(
				objectPublication.username,
				objectPublication.userName,
				...storyUsers.reduce((values: any[], source: any) => values.concat([
					source?.username,
					source?.userName,
				]), []),
			);
			const userPhoto = firstDefined(
				objectPublication.user_photo,
				objectPublication.userPhoto,
				objectPublication.profile_photo,
				objectPublication.profilePhoto,
				...storyUsers.reduce((values: any[], source: any) => values.concat([
					source?.profile_photo,
					source?.profilePhoto,
					source?.photo,
				]), []),
			);

			let user_object = {
				user_id: userId,
				profile_id: profileId,
				username,
				profile_photo: {
					url: userPhoto
				}
			};

			let found = this.UserProfileList.find(element =>
				(user_object.user_id != null && element.user_id == user_object.user_id)
				|| (user_object.user_id == null && user_object.profile_id != null && element.profile_id == user_object.profile_id)
			);
			if (found != undefined) {
				found.profile_id = profileId;
				found.profile_photo = userPhoto;
				resolve(found);
			}
			else {
				let userProfile = new SellerProfile(user_object);
				this.UserProfileList.push(userProfile);
				resolve(userProfile);
			}
		});
	}

	public getReactionClass(reactionInfo: { reaction_group_id: number, reaction_icon: string, reaction_name: string }) {
		return new Promise<ReactionClass>((resolve) => {
			let found = this.ReactionList.find(element => element.reaction_group_id == reactionInfo.reaction_group_id);
			if (found != undefined) {
				resolve(found);
			}
			else {
				let reaction = new ReactionClass(reactionInfo);
				this.ReactionList.push(reaction);
				resolve(reaction);
			}
		});
	}

	public getReactionContainer_Publications(objectPublication: any) {
		//console.log('1');
		return new Promise<Array<ReactionContainerClass>>((resolve) => {
			//console.log('2');
			let ReactionContainer = new Array<ReactionContainerClass>();
			//console.log('3');
			const reactionObjects = Array.isArray(objectPublication?.reactionContainer)
				? objectPublication.reactionContainer
				: [];
			reactionObjects.forEach(reactionObject => {
				//console.log('4');
				this.getReactionClass(reactionObject).then((reactionGroup) => {
				//	console.log('5');
					let container = new ReactionContainerClass(reactionGroup, reactionObject.reaction_quantity);
					//console.log('6');
					ReactionContainer.push(container);
				
				});
			});
			resolve(ReactionContainer);
		});
	}

	public getOwnReaction_Publications(objectPublication: any) {
		return new Promise<{ reaction_id: number, reactionClass: ReactionClass }>((resolve) => {
			if (objectPublication.ownReaction != null) {
				this.getReactionClass(objectPublication.ownReaction).then((reactionGroup) => {
					let ownReaction = {
						reaction_id: objectPublication?.ownReaction?.reaction_id,
						reactionClass: reactionGroup
					};
					resolve(ownReaction);
				});
			}
			else {
				resolve(null);
			}
		});



	}

	private syncPublicationFromServer(target: PublicationClass, source: PublicationClass): PublicationClass {
		Object.assign(target, source);
		return target;
	}

	public updatePublicationList(listName: string, objectPublication: any, setInTop?: boolean) {
		return new Promise<PublicationClass>((resolve, reject) => {
			this.getUserProfile_Publications(objectPublication).then((userProfile: SellerProfile) => {
				this.getReactionContainer_Publications(objectPublication).then((reactionContainer: Array<ReactionContainerClass>) => {
					this.getOwnReaction_Publications(objectPublication).then((ownReaction) => {
						let publication: any;
						//console.log('7');
						if (!(objectPublication instanceof PublicationClass)) {
							///console.log('8', objectPublication.publication_type);
							switch (objectPublication.publication_type) {
								case 'normal': {
									publication = new NormalPublication(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								case 'lkmusic': {
									publication = new LkmusicPublication(objectPublication, userProfile, reactionContainer, ownReaction);
									console.log('lkmusic_media',objectPublication);
								} break;
								case 'feex': {
									publication = new Feex(objectPublication, userProfile, reactionContainer, ownReaction);
									console.log('lkmusic_media',objectPublication);
								} break;
								case 'lkfilm': {
									publication = new  LkfilmPublication(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								case 'eclipse': {
									console.log('9');
									publication = new  Eventos(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								case 'land': {
									publication = new  Land(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								case 'epoch': {
									publication = new  Epoch(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								case 'earth': {
									publication = new  Earth(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								//case 'event': {
								
								//	publication = new EventPublication(objectPublication, userProfile, reactionContainer, ownReaction);
								//} break;
								case 'url': {
									publication = new UrlPublication(objectPublication, userProfile, reactionContainer, ownReaction);
								} break;
								case 'story':
                  console.log('Es historia');
									publication = new StoryPublication(objectPublication, userProfile, reactionContainer, ownReaction);
                  console.log(publication);
									break;
								default: {
								} break;
							}
							}else{
								publication = objectPublication;
							}

							// Keep the server's original author fields on Story instances.
							// PublicationClass normally stores them in userProfile, but
				// legacy Story payloads can use different field names.
				if (publication && objectPublication?.publication_type === 'story') {
					publication.user_id = objectPublication.user_id
						?? objectPublication.userId
						?? objectPublication.author_id
						?? objectPublication.authorId
						?? userProfile?.user_id
						?? publication.user_id;
					publication.profile_id = objectPublication.profile_id
						?? objectPublication.profileId
						?? userProfile?.profile_id
						?? publication.profile_id;
					publication.username = objectPublication.username
						?? objectPublication.userName
						?? userProfile?.username
						?? publication.username;
								publication.profile_photo = publication.profile_photo
									?? objectPublication.user_photo
									?? objectPublication.userPhoto
									?? objectPublication.profile_photo
									?? objectPublication.profilePhoto
									?? userProfile?.profile_photo;
								publication.__storyAuthor = objectPublication;
							}
							// this.GlobalPublicationsList.push(publication);

						switch (listName) {
							case 'MyFeedList': {
								let publicationInList = this.MyFeedList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MyFeedList.unshift(publication) : this.MyFeedList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MyFeedList);
								resolve(publication);
							} break;
							case 'MylkfilmList': {
								let publicationInList = this.MylkfilmList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MylkfilmList.unshift(publication) : this.MylkfilmList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MylkfilmList);
								resolve(publication);
							} break;
							case 'MylandList': {
								let publicationInList = this.MylandList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MylandList.unshift(publication) : this.MylandList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MylandList);
								resolve(publication);
							} break;
							case 'MyepochList': {
								let publicationInList = this.MyepochList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MyepochList.unshift(publication) : this.MyepochList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MyepochList);
								resolve(publication);
							} break;
							case 'MyeventosList': {
								let publicationInList = this.MyeventosList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MyeventosList.unshift(publication) : this.MyeventosList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MyeventosList);
								resolve(publication);
							} break;
							case 'MyearthList': {
								let publicationInList = this.MyearthList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MyearthList.unshift(publication) : this.MyearthList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MyearthList);
								resolve(publication);
							} break;
							case 'MyStoriesList': {
								let publicationInList : any = this.MyStoriesList.find(element => element.id == publication.id);
                  console.log('publicationList', publicationInList)
								if (publicationInList == undefined) {
									(setInTop) ? this.MyStoriesList.unshift(publication) : this.MyStoriesList.push(publication);
								}else{
									publicationInList.states = publication.states
					publicationInList.userProfile = publication.userProfile
					publicationInList.user_id = publication.user_id
					publicationInList.profile_id = publication.profile_id
									publicationInList.username = publication.username
									publicationInList.profile_photo = publication.profile_photo
									publicationInList.__storyAuthor = publication.__storyAuthor
									if (publication.seen === true || publicationInList.seen !== true) {
										publicationInList.seen = publication.seen;
									}
									if (publication.seen_at !== undefined && publication.seen_at !== null) {
										publicationInList.seen_at = publication.seen_at;
									}
								}
								this.orderPublicationList(this.MyStoriesList);
								this.storiesChanged$.next(this.MyStoriesList);
								resolve(publication);
							} break;
							case 'MyPublicationsList': {
								let publicationInList = this.MyPublicationsList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.MyPublicationsList.unshift(publication) : this.MyPublicationsList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.MyPublicationsList);
								resolve(publication);
							} break;
							case 'TemporalPublicationList': {
								let publicationInList = this.TemporalPublicationList.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.TemporalPublicationList.unshift(publication) : this.TemporalPublicationList.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.TemporalPublicationList);
								resolve(publication);
							} break;
							case 'COLECCIONREGALOSLIST': {
								let publicationInList = this.COLECCIONREGALOSLIST.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.COLECCIONREGALOSLIST.unshift(publication) : this.COLECCIONREGALOSLIST.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.COLECCIONREGALOSLIST);
								resolve(publication);
							} break;
							case 'COLECCIONCOMPRASLIST': {
								let publicationInList = this.COLECCIONCOMPRASLIST.find(element => element.id == publication.id);
								if (publicationInList == undefined) {
									(setInTop) ? this.COLECCIONCOMPRASLIST.unshift(publication) : this.COLECCIONCOMPRASLIST.push(publication);
								} else {
									publication = this.syncPublicationFromServer(publicationInList, publication);
								}
								this.orderPublicationList(this.COLECCIONCOMPRASLIST);
								resolve(publication);
							} break;
							default: {
								resolve(publication);
							} break;
						}
					}).catch((error) => {
						console.log('Error in getOwnReaction_Publications:', error);
						reject({ 'error': 'Error in getOwnReaction_Publications' });
					  });
					}).catch((error) => {
					  console.log('Error in getReactionContainer_Publications:', error);
					  reject({ 'error': 'Error in getReactionContainer_Publications' });
					});
				  }).catch((error) => {
					console.log('Error in getUserProfile_Publications:', error);
					reject({ 'error': 'Error in getUserProfile_Publications' });
				  });
				});
			  }

	public getPublicationByID(publication_id: number, getFromServer?: boolean) {
		getFromServer = (getFromServer) ? getFromServer : false;
		return new Promise<PublicationClass>((resolve, reject) => {
			let publication = this.GlobalPublicationsList.find(element => element.id == publication_id);
			if (publication == undefined || getFromServer == true) {

				this.getPublications(publication_id).then((data: any) => {
					this.updatePublicationList('none', data, false).then((publicationFromServer: PublicationClass) => {
						publication = publicationFromServer;

						resolve(publication);
					});
				}).catch((error) => {
					/*if (error == "Unvaliable") {
						reject(error);
					}
					else {
						console.error(error);

					}*/
					reject(error);
				});
			}
			else {
				//console.log("Recuperada de RAM");
				resolve(publication);
			}
		});
	}

	public DeleteLocalPublicationByID(publication_id: number) {
		return new Promise<string>((resolve, reject) => {
			let publication = this.MyFeedList.find(element => element.id == publication_id);

			if (publication != null) {
				let index = this.MyFeedList.indexOf(publication, 0);
				if (index > -1) {
					this.MyFeedList.splice(index, 1);
				}

				index = this.TemporalPublicationList.indexOf(publication, 0);
				if (index > -1) {
					this.TemporalPublicationList.splice(index, 1);
				}

				index = this.MyPublicationsList.indexOf(publication, 0);
				if (index > -1) {
					this.MyPublicationsList.splice(index, 1);
				}

				index = this.GlobalPublicationsList.indexOf(publication, 0);
				if (index > -1) {
					this.GlobalPublicationsList.splice(index, 1);
				}
				publication = undefined;
				resolve("Publicación eliminada de memoria");

			}
			else {
				reject("La publicación no esta en memoria");
			}

		});
	}

	public orderPublicationList(publicationsList: Array<PublicationClass>) {
		/*
		publicationsList = publicationsList.sort((p1, p2) => {
			return (p1.unixTime_created_at > p2.unixTime_created_at) ? -1 : 1;
		});
		*/
	}
	public getAllSavedPublications(page?: number, user_id?: number, publication_type?: string): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let user_id_str = (user_id) ? user_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let publication_type_str = (publication_type) ? publication_type : "";
			var params = {
					"page": page_str,
					"user_id": user_id_str,
          "publication_type": publication_type_str
			};


			this.request.createRequestGet('publications_saved', params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
         //   console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else{ 
				try {
					// Ensure the data is in the correct format for web
				   // Ensure the data is in the correct format for web
				   if (typeof data === 'string') {
					   let response = JSON.parse(data);
					//   console.log('json result publication service.ts', response);
					   resolve(response);
					 } else {
					   resolve(data);
					 }
					   }catch(e){
						   reject(e)
					   }
			}
			}).catch((error) => {
				reject(error);
			})
		});
	}
	public getAllGiftedPublications(page?: number, user_id?: number, publication_type?: string): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let user_id_str = (user_id) ? user_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let publication_type_str = (publication_type) ? publication_type : "";
			var params = {
					"page": page_str,
					"user_id": user_id_str,
          "publication_type": publication_type_str
			};


			this.request.createRequestGet('publications_gifted', params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
          //  console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else{
				try {
					// Ensure the data is in the correct format for web
				   // Ensure the data is in the correct format for web
				   if (typeof data === 'string') {
					   let response = JSON.parse(data);
					//   console.log('json result publication service.ts', response);
					   resolve(response);
					 } else {
					   resolve(data);
					 }
					   }catch(e){
						   reject(e)
					   }
			}
			}).catch((error) => {
				reject(error);
			})
		});
	}


	public getAllPaidPublications(page?: number, user_id?: number, publication_type?: string): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let user_id_str = (user_id) ? user_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let publication_type_str = (publication_type) ? publication_type : "";
			var params = {
					"page": page_str,
					"user_id": user_id_str,
          "publication_type": publication_type_str
			};


			this.request.createRequestGet('publications_paid', params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
          //  console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else{
				try {
					// Ensure the data is in the correct format for web
				   // Ensure the data is in the correct format for web
				   if (typeof data === 'string') {
					   let response = JSON.parse(data);
					//   console.log('json result publication service.ts', response);
					   resolve(response);
					 } else {
					   resolve(data);
					 }
					   }catch(e){
						   reject(e)
					   }
			}
			}).catch((error) => {
				reject(error);
			})
		});
	}
	public markPublicationNotInterested(publicationId: number): Promise<any> {
		return this.request.createRequestPost('publications/not-interested', { publication_id: publicationId });
	}

	public getAllAIPublications(
    page?: number,
    user_id?: number,
    publication_type?: string,
    authenticated?: boolean,
    cursor?: string,
    useCursor: boolean = false,
  ): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let user_id_str = (user_id) ? user_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let publication_type_str = (publication_type) ? publication_type : "";
			var params = {
					"page": page_str,
					"user_id": user_id_str,
          "publication_type": publication_type_str,
          ...(useCursor ? {
            "pagination": "cursor",
            "cursor": cursor || "",
            "limit": "8",
          } : {}),
			};

			const endpoint = authenticated ? 'publications_AI_auth' : 'publications_AI';
			this.request.createRequestGet(endpoint, params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
           // console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else{
				try {
					// Ensure the data is in the correct format for web
				   // Ensure the data is in the correct format for web
				   if (typeof data === 'string') {
					   let response = JSON.parse(data);
					  // console.log('json result publication service.ts', response);
					   resolve(response);
					 } else {
					   resolve(data);
					 }
					   }catch(e){
						   reject(e)
					   }
			}
			}).catch((error) => {
				reject(error);
			})
		});
	}

	public getUniversalLinkPublications(page?: number, publication_id?: number, publication_type?: string): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let publication_id_str = (publication_id) ? publication_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let publication_type_str = (publication_type) ? publication_type : "";
			var params = {
					"page": page_str,
					"id": publication_id_str,
          "publication_type": publication_type_str
			};


			this.request.createRequestGet('publications_UL', params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
            //console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else{
				try {
					// Ensure the data is in the correct format for web
				   // Ensure the data is in the correct format for web
				   if (typeof data === 'string') {
					   let response = JSON.parse(data);
					//   console.log('json result publication service.ts', response);
					   resolve(response);
					 } else {
					   resolve(data);
					 }
					   }catch(e){
						   reject(e)
					   }
			}
			}).catch((error) => {
				reject(error);
			})
		});
	}
	public getProfilePublications(page?: number, profile_id?: number, publication_type?: string,  pubSection?:any, orderBy = 'created_at', orderDirection: 'asc' | 'desc' = 'desc'): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let profile_id_str = (profile_id) ? profile_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
			let publication_type_str = (publication_type) ? publication_type : "";
		  let pubSection_str = (pubSection) ? pubSection : "";
		  const order_by_str = orderBy || 'created_at';
		  const order_direction_str = orderDirection === 'asc' ? 'asc' : 'desc';
			var params = {
					"page": page_str,
					"profile_id": profile_id_str,
					"user_id": profile_id_str,
          "publication_type": publication_type_str,
		  "pubSection": pubSection_str,
		  // The API owns ordering; these parameters are intentionally sent with
		  // every profile query so pagination remains newest-first server-side.
		  "order_by": order_by_str,
		  "order_direction": order_direction_str
			};


			this.request.createRequestGet('publications_Profiles', params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
          //  console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else {
				try {
			 // Ensure the data is in the correct format for web
			// Ensure the data is in the correct format for web
			if (typeof data === 'string') {
				let response = JSON.parse(data);
			//	console.log('json result publication service.ts', response);
				resolve(response);
			  } else {
				resolve(data);
			  }
				}catch(e){
					reject(e)
				}
				}
			}).catch((error) => {
				reject(error);
			})
		});
	}

	public getAllPlaylistPublications(page?: number, user_id?: number, playlist?: string): Promise<Observable<RequestResponse>> {
		return new Promise((resolve, reject) => {
			//Obtiene todas las publicaciones para mostrar en myFeed,
			//Al enviar user_id se obtienen solo publicaciones de ese usuario
			let page_str = (page) ? page.toString() : "";
			//page? page_str = page.toString(): page_str = "";
			let user_id_str = (user_id) ? user_id.toString() : "";
			//user_id? user_id_str= user_id.toString(): user_id_str = "";
      let playlist_str = (playlist) ? playlist : "";
			var params = {
					"page": page_str,
					"user_id": user_id_str,
          "playlist": playlist_str
			};


			this.request.createRequestGet('publications_from_playlist', params).then((data) => {
				if (Capacitor.getPlatform() !== 'web') {
				try {
					let json = JSON.parse(data);
					if(json){
            //console.log('json result publication service.ts', json);
						resolve(json);
					}
				}catch(e){
					reject(e)
				}
			}else{
				try {
					// Ensure the data is in the correct format for web
				   // Ensure the data is in the correct format for web
				   if (typeof data === 'string') {
					   let response = JSON.parse(data);
					 // console.log('json result publication service.ts', response);
					   resolve(response);
					 } else {
					   resolve(data);
					 }
					   }catch(e){
						   reject(e)
					   }
			}
			}).catch((error) => {
				reject(error);
			})
		});
	}

}
